/**
 * Pages through the whole highlight library.
 *
 * Deliberately its OWN module, separate from `highlightsMarkdown.js`: the
 * store (and therefore `LinkCard` / `Bookmarks`, i.e. the main chunk) needs
 * this loop, while the ~4 KB serializer is only ever needed by the /highlights
 * view. One shared module would drag the serializer into the startup chunk.
 */

/** The server clamps `limit` to 200 on both /highlights and /highlights/search. */
export const HIGHLIGHTS_EXPORT_PAGE_SIZE = 200

/** Runaway guard: one export never pages beyond this many highlights. */
export const HIGHLIGHTS_EXPORT_MAX = 5000

/**
 * @param {(page: {offset: number, limit: number}) => Promise<{items: Array, total?: number}>} fetchPage
 * @returns {Promise<{items: Array, total: number, capped: boolean}>} `capped` is
 *   true when the cap stopped us short of the server's reported total.
 */
export async function collectAllHighlights(fetchPage, { pageSize = HIGHLIGHTS_EXPORT_PAGE_SIZE, maxItems = HIGHLIGHTS_EXPORT_MAX } = {}) {
  const items = []
  let total = null
  let offset = 0

  while (items.length < maxItems) {
    const limit = Math.min(pageSize, maxItems - items.length)
    const page = await fetchPage({ offset, limit })
    const batch = Array.isArray(page?.items) ? page.items : []
    // `Number(null)` is 0 and passes isFinite, so a `total: null` in the
    // response would read as "zero highlights" and end the loop after the
    // first page — a silently truncated export reported as complete. Only a
    // real number (or a numeric string) counts as a reported total.
    const reported = page?.total == null || page.total === '' ? NaN : Number(page.total)
    if (Number.isFinite(reported)) total = reported
    items.push(...batch)
    // Advance by what came back, never by the requested limit: both endpoints
    // clamp `limit` server-side (200 today), so trusting the request would
    // silently skip rows the day that cap changes.
    if (batch.length === 0) break
    offset += batch.length
    if (total != null && items.length >= total) break
  }

  const resolvedTotal = total == null ? items.length : Math.max(total, items.length)
  return { items, total: resolvedTotal, capped: items.length < resolvedTotal }
}
